import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Assignment as TaskIcon,
} from '@mui/icons-material';

interface QuickTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSendMessage: (content: string, agentId?: string) => void;
  agents: any[];
  conversationId?: string;
}

const QuickTaskDialog: React.FC<QuickTaskDialogProps> = ({
  open,
  onClose,
  onSendMessage,
  agents,
  conversationId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [taskTitle, setTaskTitle] = useState('');

  const caseyAgent = agents.find(
    agent => agent.name === 'Casey' || agent.custom_name === 'Casey'
  );

  const handleCreateTask = () => {
    if (taskTitle.trim()) {
      // Send a message to Casey to create a task with inline form
      const message = `@Casey create a task: "${taskTitle.trim()}" - please show me the task creation form so I can fill in the details.`;
      onSendMessage(message, caseyAgent?.id);

      // Clear and close
      setTaskTitle('');
      onClose();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleCreateTask();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          bgcolor: 'background.paper',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <TaskIcon color="primary" />
          <Typography variant="h6" color="primary">
            Quick Task Creation
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Give your task a quick title. Casey will help you fill in the details with an interactive form.
          </Typography>

          <TextField
            fullWidth
            label="What needs to be done?"
            value={taskTitle}
            onChange={(e) => setTaskTitle(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., Review marketing proposal, Call client about contract"
            autoFocus
            multiline
            maxRows={3}
            InputProps={{
              startAdornment: (
                <TaskIcon sx={{ mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 1 }} />
              ),
            }}
          />

          {caseyAgent && (
            <div
              style={{
                padding: 16,
                backgroundColor: `${theme.palette.primary.main}08`,
                borderRadius: 4,
                border: `1px solid ${theme.palette.primary.main}20`,
              }}
            >
              <Typography variant="body2" color="primary">
                <strong>Casey</strong> will create an interactive task form in the chat where you can:
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" style={{ marginTop: 8 }}>
                • Set goal and deliverables<br/>
                • Choose priority level<br/>
                • Set due dates<br/>
                • Assign to team members
              </Typography>
            </div>
          )}

          {!caseyAgent && (
            <div
              style={{
                padding: 16,
                backgroundColor: `${theme.palette.warning.main}08`,
                borderRadius: 4,
                border: `1px solid ${theme.palette.warning.main}20`,
              }}
            >
              <Typography variant="body2" color="warning.main">
                Casey agent is not available. The task will be created with basic information only.
              </Typography>
            </div>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleCreateTask}
          variant="contained"
          disabled={!taskTitle.trim()}
          startIcon={<TaskIcon />}
        >
          Create with Casey
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickTaskDialog;